import React from 'react';
import PropTypes from 'prop-types';
import { makeStyles } from 'src/makeStyles';
import { useTranslation } from '../../src/i18n';
import { dateFormats, TP } from '../../constants/consts';
import { colors, fontSize } from '../../constants/styleConstants';
import { createDate } from '../../utils/date';

export const performanceStatuses = {
  regular: 'regular',
  soldout: 'soldout',
  cancelled: 'cancelled',
  livestream: 'livestream',
};

const getLabelColor = status => {
  switch (status) {
    case performanceStatuses.cancelled:
      return colors.red;
    case performanceStatuses.livestream:
      return colors.darkBlue;
    default:
      return colors.darkerGray;
  }
};

const useStyles = makeStyles()((theme, props) => ({
  root: {
    textTransform: 'uppercase',
    width: 98,
    height: 114,
    textAlign: 'center',
    fontFamily: 'Roboto',
    backgroundColor: props?.status === performanceStatuses.cancelled ? colors.redCancel : colors.darkerGray,
    color: props?.status === performanceStatuses.cancelled ? colors.white : colors.title,
  },
  status: {
    height: props?.status === performanceStatuses.regular ? 16 : 23,
    color: colors.white,
    backgroundColor: getLabelColor(props?.status),
    fontSize: fontSize.textFieldLabel,
    fontWeight: 300,
    lineHeight: '23px',
  },
  month: {
    color: props?.status === performanceStatuses.cancelled ? colors.white : colors.cancelButton,
    fontSize: fontSize.textFieldLabel,
    fontWeight: 300,
    height: 15,
    lineHeight: '15px',
    marginTop: theme.spacing(0.5),
  },
  day: {
    fontSize: fontSize.title,
    height: 38,
    lineHeight: '38px',
    verticalAlign: 'middle',
  },
  dayOfWeek: {
    fontSize: fontSize.textFieldLabel,
    fontWeight: 'bold',
    height: 15,
    lineHeight: '15px',
  },
  typeLetter: {
    fontSize: 12,
    fontStyle: 'italic',
    textTransform: 'lowercase',
  },
}));

const getPerformanceStatusLabel = status => {
  switch (status) {
    case performanceStatuses.soldout:
      return `${TP}.PERFORMANCE_SOLDOUT`;
    case performanceStatuses.cancelled:
      return `${TP}.PERFORMANCE_CANCELLED`;
    case performanceStatuses.livestream:
      return `${TP}.FN_LIVESTREAM`;
    default:
      return null;
  }
};

const PerformanceDate = props => {
  const { date, className, status, matinee, tour } = props;
  const { classes, cx } = useStyles(props);
  const { t } = useTranslation();

  const momentDate = createDate(date, dateFormats.basic);
  const month = momentDate.format(dateFormats.productionMonth);
  const day = momentDate.format(dateFormats.date);
  const dayOfWeek = momentDate.format(dateFormats.dayOfWeekShort);
  const statusTitle = getPerformanceStatusLabel(status);

  return (
    <div className={cx(classes.root, className)}>
      <div className={classes.status}>{statusTitle && t(statusTitle)}</div>
      <div className={classes.month}>{month}</div>
      <div className={classes.day}>
        {day}
        <span className={classes.typeLetter}>{matinee && 'm'}</span>
        <span className={classes.typeLetter}>{tour && 't'}</span>
      </div>
      <div className={classes.dayOfWeek}>{dayOfWeek}</div>
    </div>
  );
};

PerformanceDate.propTypes = {
  className: PropTypes.string,
  date: PropTypes.string.isRequired,
  status: PropTypes.oneOf(Object.values(performanceStatuses)),
  matinee: PropTypes.bool,
};

PerformanceDate.defaultProps = {
  status: performanceStatuses.regular,
};

export default PerformanceDate;
